import React, { Fragment, FunctionComponent, useEffect, useRef, useState } from 'react';
import { ApplicationState } from '../../../redux/States';
import { ErrorDispatches } from '../../../redux/Dispatches';
import { ConnectedProps, connect } from 'react-redux';
import { Cell, Row, downloadSheetChartAsPDFByPrint, downloadSheets, generateSheet } from '../../../utils';
import {
    Col,
    Dropdown,
    DropdownButton,
    Row as BSRow,
    Table,
} from 'react-bootstrap';
import { AuthedLayout } from '../../../components';
import { StudentInjury, StudentGradeNoSeat } from '../../../model';
import { ChartState, useApexBarChart, useDownloadSheetChart, useMonth, useSemGrade } from '../../../hook';
import apis from '../../../apis';
import { I18N } from '../../../i18n-raw';
import { toast } from 'react-toastify';
type PageData = StudentInjury & StudentGradeNoSeat;

const borderStyle = { color: { rgb: '000000' }, style: 'thin' } as const;
const fullBorderStyle = {
    top: borderStyle,
    bottom: borderStyle,
    left: borderStyle,
    right: borderStyle,
} as const;
const cellAlignStyle = {
    horizontal: 'center',
    vertical: 'center',
} as const;

const mapState = (state: ApplicationState) => ({ ...state.auth });
const mapDispatches = ErrorDispatches;

const connector = connect(mapState, mapDispatches);

type Props = ConnectedProps<typeof connector>;

interface CountData {
    male: number;
    female: number;
    total: number;
}

interface MonthData {
    "時間": { [key: string]: CountData };
    "地點": { [key: string]: CountData };
    "受傷部位": {
        head: CountData;
        eye: CountData;
        mouth: CountData;
        face: CountData;
        nose: CountData;
        chest: CountData;
        belly: CountData;
        back: CountData;
        shoulder: CountData;
        arm: CountData;
        waist: CountData;
        leg: CountData;
        neck: CountData;
        buttock: CountData;
        perineum: CountData;
    };
    "意外傷害": {
        friction: CountData;
        slash: CountData;
        press: CountData;
        strick: CountData;
        twist: CountData;
        burn: CountData;
        sting: CountData;
        fracture: CountData;
        old: CountData;
        aOther: CountData;
    };
    "症狀": {
        fever: CountData;
        dizzy: CountData;
        puke: CountData;
        headache: CountData;
        toothache: CountData;
        stomachache: CountData;
        bellyache: CountData;
        diarrhoea: CountData;
        menses: CountData;
        pant: CountData;
        noseBlood: CountData;
        rash: CountData;
        eyeache: CountData;
        hOther: CountData;
    };
    "處理方式": {
        woundCare: CountData;
        ice: CountData;
        hot: CountData;
        rest: CountData;
        noteParent: CountData;
        backHome: CountData;
        hospital: CountData;
        education: CountData;
        mOther: CountData;
    };
    "觀察時間-分": { time: CountData };
}

const injuryAccStaticMonthChart: FunctionComponent<Props> = ({
    user,
    catchErrorForModal,
}) => {
    const { yearSem, element: semGradeElement } = useSemGrade();
    const { selectedmonth, element: element } = useMonth({
        yearSem,
    });
    const [datas, setStudents] = useState<PageData[]>([]);
    const chartRef2 = useRef<HTMLDivElement>(null);
    const chartRef3 = useRef<HTMLDivElement>(null);
    const chartRef4 = useRef<HTMLDivElement>(null);
    const chartRef5 = useRef<HTMLDivElement>(null);
    const chartRef6 = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (yearSem && selectedmonth) {
            toast
                .promise(
                    apis.getInjuryAccStaticMonthChart(yearSem.year, selectedmonth.id)
                        .then((r) =>
                            setStudents(r.map(({ studentInjury, ...s }) => ({
                                ...studentInjury,
                                ...s
                            })))
                        ),
                    {
                        pending: '資料載入中......',
                        success: '資料載入成功！',
                        error: '資料載入失敗！請查看錯誤資訊。',
                    }
                )
        }
    }, [selectedmonth, yearSem?.year, yearSem?.sem]);
    const initializeCountData = (): CountData => ({
        male: 0,
        female: 0,
        total: 0
    });

    const initializeMonthData = (): MonthData => ({
        "時間": { "上午": initializeCountData(), "中午": initializeCountData(), "下午": initializeCountData() },
        "地點": {
            "操場": initializeCountData(),
            "遊戲運動器材": initializeCountData(),
            "普通教室": initializeCountData(),
            "專科教室": initializeCountData(),
            "走廊": initializeCountData(),
            "樓梯": initializeCountData(),
            "地下室": initializeCountData(),
            "體育館活動中心": initializeCountData(),
            "廁所": initializeCountData(),
            "校外": initializeCountData(),
            "其它": initializeCountData(),
        },
        "受傷部位": {
            head: initializeCountData(),
            eye: initializeCountData(),
            mouth: initializeCountData(),
            face: initializeCountData(),
            nose: initializeCountData(),
            chest: initializeCountData(),
            belly: initializeCountData(),
            back: initializeCountData(),
            shoulder: initializeCountData(),
            arm: initializeCountData(),
            waist: initializeCountData(),
            leg: initializeCountData(),
            neck: initializeCountData(),
            buttock: initializeCountData(),
            perineum: initializeCountData()
        },
        "意外傷害": {
            friction: initializeCountData(),
            slash: initializeCountData(),
            press: initializeCountData(),
            strick: initializeCountData(),
            twist: initializeCountData(),
            burn: initializeCountData(),
            sting: initializeCountData(),
            fracture: initializeCountData(),
            old: initializeCountData(),
            aOther: initializeCountData()
        },
        "症狀": {
            fever: initializeCountData(),
            dizzy: initializeCountData(),
            puke: initializeCountData(),
            headache: initializeCountData(),
            toothache: initializeCountData(),
            stomachache: initializeCountData(),
            bellyache: initializeCountData(),
            diarrhoea: initializeCountData(),
            menses: initializeCountData(),
            pant: initializeCountData(),
            noseBlood: initializeCountData(),
            rash: initializeCountData(),
            eyeache: initializeCountData(),
            hOther: initializeCountData()
        },
        "處理方式": {
            woundCare: initializeCountData(),
            ice: initializeCountData(),
            hot: initializeCountData(),
            rest: initializeCountData(),
            noteParent: initializeCountData(),
            backHome: initializeCountData(),
            hospital: initializeCountData(),
            education: initializeCountData(),
            mOther: initializeCountData()
        },
        "觀察時間-分": {
            time: initializeCountData(),
        }
    });

    function organizeDataBySex(data: PageData[]): MonthData {
        const monthData: MonthData = initializeMonthData();

        let obsMale = 0;
        let obsFemale = 0;
        let obsTotal = 0;

        const toNumber = (v: unknown): number => {
            if (v == null || v === '') return 0;
            const n = typeof v === 'number' ? v : Number(v);
            return Number.isFinite(n) ? n : 0;
        };

        data.forEach((record) => {
            const isMale = record.sex === '1';
            const sexKey = isMale ? 'male' : 'female';

            const momentEnum = record.momentEnum;

            if (momentEnum !== null && momentEnum !== undefined) {
                const momentKey = I18N.InjuryMoment[record.momentEnum];

                if (momentKey) {
                    monthData['時間'][momentKey][sexKey]++;
                    monthData['時間'][momentKey].total++;
                }
            }
            // if (record.momentEnum) {
            //     const momentKey = I18N.InjuryMoment[record.momentEnum];
            //     monthData['時間'][momentKey][sexKey]++;
            //     monthData['時間'][momentKey].total++;
            // }

            const placeKey = I18N.InjuryPlace[record.placeEnum];
            if (monthData['地點'][placeKey]) {
                monthData['地點'][placeKey][sexKey]++;
                monthData['地點'][placeKey].total++;
            }

            const mins = toNumber(record.observationMins);
            if (isMale) obsMale += mins; else obsFemale += mins;
            obsTotal += mins;

            const countInjuries = (injuryCategory: keyof MonthData) => {
                const categoryData = monthData[injuryCategory] as any;

                const injuryKeys = Object.keys(record) as (
                    | keyof MonthData['受傷部位']
                    | keyof MonthData['意外傷害']
                    | keyof MonthData['症狀']
                    | keyof MonthData['處理方式']
                )[];

                injuryKeys.forEach((key) => {
                    if (
                        Object.prototype.hasOwnProperty.call(categoryData, key) &&
                        (record as any)[key]
                    ) {
                        categoryData[key][sexKey]++;
                        categoryData[key].total++;
                    }
                });

                if (injuryCategory === '處理方式' && (record as any).mOther && categoryData.mOther) {
                    categoryData.mOther[sexKey] = (categoryData.mOther[sexKey] ?? 0) + 1;
                    categoryData.mOther.total = (categoryData.mOther.total ?? 0) + 1;
                }
            };

            countInjuries('受傷部位');
            countInjuries('意外傷害');
            countInjuries('症狀');
            countInjuries('處理方式');
        });

        monthData['觀察時間-分']['time'].male = obsMale;
        monthData['觀察時間-分']['time'].female = obsFemale;
        monthData['觀察時間-分']['time'].total = obsTotal;

        return monthData;
    }

    const organizedData = organizeDataBySex(datas);

    const contentRows: Row[] = Object.values(organizedData).flatMap(
        (gs, idx, allArray) => {
            const itemrows = Object.values(gs).flatMap((g: any, id) => {
                return InjuryToRows(
                    Object.keys(organizedData)[idx],
                    Object.keys(I18N.Injury).includes(Object.keys(gs)[id]) ? I18N.Injury[Object.keys(gs)[id]] : Object.keys(gs)[id],
                    g,
                    id,
                    (Object.keys(gs).length)
                );
            });
            return itemrows;
        }
    );

    const content: Row[] = [
        {
            cells: [
                {
                    value: '性別',
                    merge: { column: 3 },
                    style: { border: fullBorderStyle },
                },
                {
                    value: '男',
                    merge: { column: 1 },
                    style: { border: fullBorderStyle },
                },
                {
                    value: '女',
                    merge: { column: 1 },
                    style: { border: fullBorderStyle },
                },
                {
                    value: '合計',
                    merge: { column: 1 },
                    style: { border: fullBorderStyle },
                },
            ],
        },
        ...contentRows,
    ];
    const safe = (v: any) => Number.isFinite(v) ? v : 0;

    const commonBarOptions = {
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: '55%',
                dataLabels: { position: 'top' }
            }
        },
        dataLabels: {
            enabled: true,
            offsetY: -8,
            style: {
                fontSize: '14px',
                fontWeight: 'bold',
                colors: ['#000']
            }
        },
        colors: [
            '#4F81BD', '#C0504D', '#9BBB59', '#8064A2', '#4BACC6',
            '#F79646', '#92A9CF', '#D99694', '#C3D69B', '#B2A2C7',
            '#7F6084', '#31859B', '#E46C0A', '#77933C', '#60497A'
        ],
        legend: {
            fontSize: '13px',
            markers: { radius: 2 }
        },
        grid: {
            strokeDashArray: 4
        }
    };

    const Placestate: ChartState = {
        options: {
            chart: { id: 'placechart' },
            ...commonBarOptions,
            xaxis: {
                title: { text: "受傷地點" },
                categories: ["操場", "遊戲運動器材", "普通教室", "專科教室", "走廊", "樓梯", "地下室", "體育館活動中心", "廁所", "校外", "其它"]
            },
            yaxis: { title: { text: "人數" } }
        },
        series: [
            {
                name: "男",
                data: [
                    safe(organizedData["地點"]["操場"].male),
                    safe(organizedData["地點"]["遊戲運動器材"].male),
                    safe(organizedData["地點"]["普通教室"].male),
                    safe(organizedData["地點"]["專科教室"].male),
                    safe(organizedData["地點"]["走廊"].male),
                    safe(organizedData["地點"]["樓梯"].male),
                    safe(organizedData["地點"]["地下室"].male),
                    safe(organizedData["地點"]["體育館活動中心"].male),
                    safe(organizedData["地點"]["廁所"].male),
                    safe(organizedData["地點"]["校外"].male),
                    safe(organizedData["地點"]["其它"].male)
                ]
            },
            {
                name: "女",
                data: [
                    safe(organizedData["地點"]["操場"].female),
                    safe(organizedData["地點"]["遊戲運動器材"].female),
                    safe(organizedData["地點"]["普通教室"].female),
                    safe(organizedData["地點"]["專科教室"].female),
                    safe(organizedData["地點"]["走廊"].female),
                    safe(organizedData["地點"]["樓梯"].female),
                    safe(organizedData["地點"]["地下室"].female),
                    safe(organizedData["地點"]["體育館活動中心"].female),
                    safe(organizedData["地點"]["廁所"].female),
                    safe(organizedData["地點"]["校外"].female),
                    safe(organizedData["地點"]["其它"].female)
                ]
            }
        ],
        charttype: 'bar'
    };
    const Partstate: ChartState = {
        options: {
            chart: { id: 'partchart' },
            ...commonBarOptions,
            xaxis: {
                title: { text: "受傷部位" },
                categories: ["頭", "頸", "肩", "胸", "腹", "背", "眼", "顏面", "口腔", "耳鼻喉", "上肢", "腰", "下肢", "臀部", "會陰部"]
            },
            yaxis: { title: { text: "人數" } }
        },
        series: [
            {
                name: "男", data: [
                    safe(organizedData["受傷部位"].head.male),
                    safe(organizedData["受傷部位"].neck.male),
                    safe(organizedData["受傷部位"].shoulder.male),
                    safe(organizedData["受傷部位"].chest.male),
                    safe(organizedData["受傷部位"].belly.male),
                    safe(organizedData["受傷部位"].back.male),
                    safe(organizedData["受傷部位"].eye.male),
                    safe(organizedData["受傷部位"].face.male),
                    safe(organizedData["受傷部位"].mouth.male),
                    safe(organizedData["受傷部位"].nose.male),
                    safe(organizedData["受傷部位"].arm.male),
                    safe(organizedData["受傷部位"].waist.male),
                    safe(organizedData["受傷部位"].leg.male),
                    safe(organizedData["受傷部位"].buttock.male),
                    safe(organizedData["受傷部位"].perineum.male)
                ]
            },
            {
                name: "女", data: [
                    safe(organizedData["受傷部位"].head.female),
                    safe(organizedData["受傷部位"].neck.female),
                    safe(organizedData["受傷部位"].shoulder.female),
                    safe(organizedData["受傷部位"].chest.female),
                    safe(organizedData["受傷部位"].belly.female),
                    safe(organizedData["受傷部位"].back.female),
                    safe(organizedData["受傷部位"].eye.female),
                    safe(organizedData["受傷部位"].face.female),
                    safe(organizedData["受傷部位"].mouth.female),
                    safe(organizedData["受傷部位"].nose.female),
                    safe(organizedData["受傷部位"].arm.female),
                    safe(organizedData["受傷部位"].waist.female),
                    safe(organizedData["受傷部位"].leg.female),
                    safe(organizedData["受傷部位"].buttock.female),
                    safe(organizedData["受傷部位"].perineum.female)
                ]
            }
        ],
        charttype: 'bar'
    };

    const Injurystate: ChartState = {
        options: {
            chart: { id: 'injurychart' },
            ...commonBarOptions,
            xaxis: {
                title: { text: "意外傷害" },
                categories: ["擦傷", "裂割刺傷", "夾壓傷", "挫撞傷", "扭傷", "灼燙傷", "叮咬傷", "骨折", "舊傷", "外科其它"]
            },
            yaxis: { title: { text: "人數" } }
        },
        series: [
            {
                name: "男",
                data: [
                    safe(organizedData["意外傷害"].friction.male),
                    safe(organizedData["意外傷害"].slash.male),
                    safe(organizedData["意外傷害"].press.male),
                    safe(organizedData["意外傷害"].strick.male),
                    safe(organizedData["意外傷害"].twist.male),
                    safe(organizedData["意外傷害"].burn.male),
                    safe(organizedData["意外傷害"].sting.male),
                    safe(organizedData["意外傷害"].fracture.male),
                    safe(organizedData["意外傷害"].old.male),
                    safe(organizedData["意外傷害"].aOther.male)
                ]
            },
            {
                name: "女",
                data: [
                    safe(organizedData["意外傷害"].friction.female),
                    safe(organizedData["意外傷害"].slash.female),
                    safe(organizedData["意外傷害"].press.female),
                    safe(organizedData["意外傷害"].strick.female),
                    safe(organizedData["意外傷害"].twist.female),
                    safe(organizedData["意外傷害"].burn.female),
                    safe(organizedData["意外傷害"].sting.female),
                    safe(organizedData["意外傷害"].fracture.female),
                    safe(organizedData["意外傷害"].old.female),
                    safe(organizedData["意外傷害"].aOther.female)
                ]
            }
        ],
        charttype: 'bar'
    };
    const Hurtstate: ChartState = {
        options: {
            chart: { id: 'hurtchart' },
            ...commonBarOptions,
            xaxis: {
                title: { text: "症狀" },
                categories: ["發燒", "暈眩", "噁心嘔吐", "頭痛", "牙痛", "胃痛", "腹痛", "腹瀉", "經痛", "氣喘", "流鼻血", "疹癢", "眼疾", "內科其它"]
            },
            yaxis: { title: { text: "人數" } }
        },
        series: [
            {
                name: "男",
                data: [
                    safe(organizedData["症狀"].fever.male),
                    safe(organizedData["症狀"].dizzy.male),
                    safe(organizedData["症狀"].puke.male),
                    safe(organizedData["症狀"].headache.male),
                    safe(organizedData["症狀"].toothache.male),
                    safe(organizedData["症狀"].stomachache.male),
                    safe(organizedData["症狀"].bellyache.male),
                    safe(organizedData["症狀"].diarrhoea.male),
                    safe(organizedData["症狀"].menses.male),
                    safe(organizedData["症狀"].pant.male),
                    safe(organizedData["症狀"].noseBlood.male),
                    safe(organizedData["症狀"].rash.male),
                    safe(organizedData["症狀"].eyeache.male),
                    safe(organizedData["症狀"].hOther.male)
                ]
            },
            {
                name: "女",
                data: [
                    safe(organizedData["症狀"].fever.female),
                    safe(organizedData["症狀"].dizzy.female),
                    safe(organizedData["症狀"].puke.female),
                    safe(organizedData["症狀"].headache.female),
                    safe(organizedData["症狀"].toothache.female),
                    safe(organizedData["症狀"].stomachache.female),
                    safe(organizedData["症狀"].bellyache.female),
                    safe(organizedData["症狀"].diarrhoea.female),
                    safe(organizedData["症狀"].menses.female),
                    safe(organizedData["症狀"].pant.female),
                    safe(organizedData["症狀"].noseBlood.female),
                    safe(organizedData["症狀"].rash.female),
                    safe(organizedData["症狀"].eyeache.female),
                    safe(organizedData["症狀"].hOther.female)
                ]
            }
        ],
        charttype: 'bar'
    };

    const Treatmentstate: ChartState = {
        options: {
            chart: { id: 'treatmentchart' },
            ...commonBarOptions,
            xaxis: {
                title: { text: "處理方式" },
                categories: ["傷口護理", "冰敷", "熱敷", "休息觀察", "通知家長", "家長帶回", "校方送醫", "衛生教育", "其它處理"]
            },
            yaxis: { title: { text: "人數" } }
        },
        series: [
            {
                name: "男",
                data: [
                    safe(organizedData["處理方式"].woundCare.male),
                    safe(organizedData["處理方式"].ice.male),
                    safe(organizedData["處理方式"].hot.male),
                    safe(organizedData["處理方式"].rest.male),
                    safe(organizedData["處理方式"].noteParent.male),
                    safe(organizedData["處理方式"].backHome.male),
                    safe(organizedData["處理方式"].hospital.male),
                    safe(organizedData["處理方式"].education.male),
                    safe(organizedData["處理方式"].mOther.male)
                ]
            },
            {
                name: "女",
                data: [
                    safe(organizedData["處理方式"].woundCare.female),
                    safe(organizedData["處理方式"].ice.female),
                    safe(organizedData["處理方式"].hot.female),
                    safe(organizedData["處理方式"].rest.female),
                    safe(organizedData["處理方式"].noteParent.female),
                    safe(organizedData["處理方式"].backHome.female),
                    safe(organizedData["處理方式"].hospital.female),
                    safe(organizedData["處理方式"].education.female),
                    safe(organizedData["處理方式"].mOther.female)
                ]
            }
        ],
        charttype: 'bar'
    };


    const tableworkSheet: Row[] = [
        {
            cells: [
                {
                    value: `${yearSem?.year}學年${selectedmonth?.name}學生傷病分析`,
                    style: { alignment: { horizontal: 'left' } },
                },
            ],
        },
        {
            cells: [
                {
                    value: '性別',
                    merge: { column: 3 },
                    style: { border: fullBorderStyle },
                },
                {
                    value: '男',
                    merge: { column: 1 },
                    style: { border: fullBorderStyle },
                },
                {
                    value: '女',
                    merge: { column: 1 },
                    style: { border: fullBorderStyle },
                },
                {
                    value: '合計',
                    merge: { column: 1 },
                    style: { border: fullBorderStyle },
                },
            ],
        },
        ...contentRows,
        {
            cells: [
                {
                    value:
                        '承辦人:　　　　　　　　　　　　　組長:　　　　　　　　　　　　　主任:　　　　　　　　　　　　　　校長:　　　　　　　　　　　　',
                    style: { alignment: { horizontal: 'left' } },
                },
            ],
        }
    ];
    const { element: partchart } = useApexBarChart(Partstate);
    const { element: placechart } = useApexBarChart(Placestate);
    const { element: injurychart } = useApexBarChart(Injurystate);
    const { element: hurtchart } = useApexBarChart(Hurtstate);
    const { element: treatmentchart } = useApexBarChart(Treatmentstate);
    const titles = [`${user.schools[user.currentSchool].name}${yearSem?.year}學年${selectedmonth?.name}學生傷病分析`];
    const chartRefs = [{ ref: 'partchart', sheetname: "受傷部位" }, { ref: 'placechart', sheetname: "受傷地點" }, { ref: 'injurychart', sheetname: "意外傷害" }, { ref: "hurtchart", sheetname: "症狀" }, { ref: "treatmentchart", sheetname: "處理方式" }];
    const { downloadChart } = useDownloadSheetChart({
        chartdata: [
            organizedData["時間"]["上午"]["total"],
            organizedData["時間"]["中午"]["total"],
            organizedData["時間"]["下午"]["total"]
        ],
        chartRefs,
        titles,
        tablesheets: content,
        tablesheetname: "學年報表"
    });
    return (
        <AuthedLayout>
            <BSRow className="align-items-center">
                <Col xs={3} className="mr-3">
                    {semGradeElement}
                </Col>

                <Col xs={4} className="mr-4">
                    {element}
                </Col>

                <Col className="d-flex justify-content-end">
                    <DropdownButton
                        id="injury-acc-static-month-export"
                        title="匯出"
                        variant="success"
                        disabled={!datas.length}
                    >
                        <Dropdown.Item
                            onClick={() => {
                                downloadChart();
                            }}
                        >
                            Excel 下載
                        </Dropdown.Item>

                        <Dropdown.Item
                            onClick={() => {
                                downloadSheetChartAsPDFByPrint({
                                    title: `${yearSem?.year}學年${selectedmonth?.name}學生傷病分析`,
                                    tableElementId: 'injury-table',

                                    // 月份統計表欄位不多，表格使用直式
                                    firstPageOrientation: 'portrait',

                                    // 圖表維持橫式
                                    chartPageOrientation: 'landscape',

                                    tableAsImage: true,
                                    tableImageScale: 2,

                                    charts: [
                                        {
                                            id: 'partchart',
                                            title: '受傷部位',
                                        },
                                        {
                                            id: 'placechart',
                                            title: '受傷地點',
                                        },
                                        {
                                            id: 'injurychart',
                                            title: '意外傷害',
                                        },
                                        {
                                            id: 'hurtchart',
                                            title: '症狀',
                                        },
                                        {
                                            id: 'treatmentchart',
                                            title: '處理方式',
                                        },
                                    ],
                                });
                            }}
                        >
                            列印
                        </Dropdown.Item>
                    </DropdownButton>
                </Col>
            </BSRow>

            <hr />
            <hr />
            <BSRow>
                <Table
                    id="injury-table"
                    striped
                    bordered
                    className="text-center"
                    style={{ tableLayout: 'fixed' }}
                >
                    <tbody>
                        {content.map((r, i) => {
                            const cells = r.cells.map((c, j) =>
                                c.value != undefined ? (
                                    <td
                                        colSpan={c.merge?.column}
                                        rowSpan={c.merge?.row}
                                        key={`${i}-${j}`}
                                    >
                                        {c.value}
                                    </td>
                                ) : (
                                    <Fragment key={`${i}-${j}`}></Fragment>
                                )
                            );
                            return <tr key={i}>{cells}</tr>;
                        })}
                    </tbody>
                </Table>
            </BSRow>
            <BSRow>
                <div ref={chartRef2} data-html2canvas-ignore>
                    {partchart}
                </div>
                <div ref={chartRef3} data-html2canvas-ignore>
                    {placechart}
                </div>
                <div ref={chartRef4} data-html2canvas-ignore>
                    {injurychart}
                </div>
                <div ref={chartRef5} data-html2canvas-ignore>
                    {hurtchart}
                </div>
                <div ref={chartRef6} data-html2canvas-ignore>
                    {treatmentchart}
                </div>
            </BSRow>
        </AuthedLayout>
    );
};

function InjuryToRows(
    type: string,
    item: string,
    g: { male: number, female: number, total: number },
    id: number,
    itemlength: number
): Row[] {
    const typeNo = [];

    if (id == 0 && type != "觀察時間-分" && type != "意外傷害" && type != "症狀") {
        typeNo.push({
            value: id == 0 ? type : undefined,
            merge: { row: itemlength, column: 1 },
            style: { border: fullBorderStyle },
        })
    }
    if (id == 0 && type == "觀察時間-分") {
        typeNo.push({
            value: id == 0 ? type : undefined,
            merge: { row: itemlength, column: 3 },
            style: { border: fullBorderStyle },
        })
    }
    if (type != "意外傷害" && type != "症狀" && type != "觀察時間-分") {
        typeNo.push({
            value: item,
            merge: { row: 1, column: 2 },
            style: { border: fullBorderStyle },
        });
    }
    if (type == "意外傷害" && id == 0) {
        typeNo.push({
            value: "受傷種類",
            merge: { row: 24, column: 1 },
            style: { border: fullBorderStyle },
        });
    }
    if ((type == "症狀" || type == "意外傷害") && id == 0) {
        typeNo.push(
            {
                value: type,
                merge: { row: itemlength, column: 1 },
                style: { border: fullBorderStyle },
            },
            {
                value: item,
                merge: { row: 1, column: 1 },
                style: { border: fullBorderStyle },
            });
    }
    if ((type == "意外傷害" || type == "症狀") && id > 0) {
        typeNo.push({
            value: item,
            merge: { row: 1, column: 1 },
            style: { border: fullBorderStyle },
        });
    }
    return [
        {
            cells: [
                ...typeNo,
                {
                    value: g.male ?? 0,
                    merge: {},
                    style: { border: { left: borderStyle } },
                },
                {
                    value: g.female ?? 0,
                    merge: {},
                    style: { border: { left: borderStyle } },
                },
                {
                    value: g.total,
                    merge: {},
                    style: { border: { right: borderStyle } },
                },

            ],
        },

    ];
}

export const InjuryAccStaticMonthChart = connector(injuryAccStaticMonthChart);
